(() => {
  'use strict';

  const testUrls = [
    { key: 'refresh-rate', url: '/pages/deep-lab-refresh-rate' },
    { key: 'dead-pixel', url: '/pages/deep-lab-dead-pixel' },
    { key: 'ghosting', url: '/pages/deep-lab-ghosting' },
    { key: 'motion-blur', url: '/pages/deep-lab-motion-blur' },
    { key: 'reaction-time', url: '/pages/deep-lab-reaction-time' },
    { key: 'frame-pacing', url: '/pages/deep-lab-frame-pacing' },
    { key: 'mouse-accuracy', url: '/pages/deep-lab-mouse-accuracy' },
    { key: 'cps', url: '/pages/deep-lab-cps' },
    { key: 'double-click', url: '/pages/deep-lab-double-click' },
    { key: 'keyboard', url: '/pages/deep-lab-keyboard' },
    { key: 'hdr', url: '/pages/deep-lab-hdr' },
    { key: 'color-banding', url: '/pages/deep-lab-color-banding' },
    { key: 'backlight', url: '/pages/deep-lab-backlight' },
    { key: 'system-benchmark', url: '/pages/deep-lab-system-benchmark' },
    { key: 'polling-rate', url: '/pages/deep-lab-polling-rate' },
  ];

  const interactiveSelector =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';

  const isTyping = (element) =>
    !!element && (element.matches('input,textarea,select') || element.isContentEditable);

  const isVisible = (element) => !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);

  function focusMain(root) {
    const main = root.querySelector('[data-dg-lab-main]') || root.querySelector('main');
    if (!main) return;

    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    main.focus({ preventScroll: true });
    main.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function moveInGroup(root, current, direction) {
    if (!root.contains(current)) return false;

    const group = current.closest('.dg-lab-app-grid,.related-tests,.actions,.dg-lab-tool-guide');
    if (!group || !root.contains(group)) return false;

    const items = [...group.querySelectorAll(interactiveSelector)].filter(isVisible);
    const index = items.indexOf(current);
    if (index < 0 || items.length < 2) return false;

    const columns = group.classList.contains('dg-lab-app-grid')
      ? Math.max(1, Math.round(group.clientWidth / Math.max(1, current.getBoundingClientRect().width)))
      : 1;

    let next = index;
    if (direction === 'next') next = (index + 1) % items.length;
    if (direction === 'prev') next = (index - 1 + items.length) % items.length;
    if (direction === 'down') next = Math.min(items.length - 1, index + columns);
    if (direction === 'up') next = Math.max(0, index - columns);

    items[next].focus();
    return true;
  }

  function initLab(root) {
    if (root.dataset.dgLabCommonInitialized === 'true') return;
    root.dataset.dgLabCommonInitialized = 'true';

    const controller = new AbortController();
    root.dgLabCommonCleanup = () => controller.abort();

    document.addEventListener(
      'keydown',
      (event) => {
        const target = event.target;
        if (!root.contains(target) && target !== document.body) return;

        if (event.key === 'Escape' && document.fullscreenElement && root.contains(document.fullscreenElement)) {
          document.exitFullscreen().catch(() => {});
          return;
        }

        if (event.altKey && !event.ctrlKey && !event.metaKey) {
          const key = event.key.toLowerCase();

          if (key === 'h') {
            event.preventDefault();
            window.location.href = '/pages/deep-lab';
            return;
          }

          if (key === 't') {
            event.preventDefault();
            focusMain(root);
            return;
          }

          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const currentKey = root.dataset.dgLabTool;
            const index = testUrls.findIndex((item) => item.key === currentKey);
            if (index >= 0) {
              event.preventDefault();
              const delta = event.key === 'ArrowRight' ? 1 : -1;
              window.location.href = testUrls[(index + delta + testUrls.length) % testUrls.length].url;
            }
          }
        }

        if (isTyping(target)) return;

        if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key) && root.contains(target)) {
          const map = { ArrowRight: 'next', ArrowLeft: 'prev', ArrowDown: 'down', ArrowUp: 'up' };
          if (moveInGroup(root, target, map[event.key])) event.preventDefault();
        }

        if ((event.key === ' ' || event.key === 'Enter') && target.matches('[role="button"]:not(button):not(a)')) {
          event.preventDefault();
          target.click();
        }
      },
      { signal: controller.signal }
    );

    root.querySelectorAll('.dg-lab-app-grid,.related-tests').forEach((group) => {
      group.setAttribute('aria-label', group.classList.contains('dg-lab-app-grid') ? 'Listado de tests' : 'Tests relacionados');
      group.querySelectorAll(':scope > a').forEach((element) => element.setAttribute('aria-setsize', group.children.length));
    });

    root.querySelectorAll('canvas').forEach((canvas) => {
      if (!canvas.hasAttribute('role')) canvas.setAttribute('role', 'img');
      if (!canvas.hasAttribute('aria-label')) canvas.setAttribute('aria-label', 'Representacion grafica del resultado del test');
    });

    root.querySelectorAll('.status').forEach((element) => {
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      element.setAttribute('aria-atomic', 'true');
    });

    root.querySelectorAll('button').forEach((button) => {
      if (!button.hasAttribute('type')) button.type = 'button';
    });

    const main = root.querySelector('[data-dg-lab-main]') || root;
    if (!main.querySelector('#dg-lab-keyboard-instructions')) {
      const hint = document.createElement('p');
      hint.className = 'sr-only';
      hint.id = 'dg-lab-keyboard-instructions';
      hint.textContent =
        'Navegacion por teclado del LAB: usa Tab y Mayusculas mas Tab para avanzar o retroceder. En grupos de opciones puedes usar las flechas. Alt mas H vuelve al inicio, Alt mas T salta al contenido y Alt mas flecha izquierda o derecha cambia de test.';
      main.prepend(hint);

      const existing = main.getAttribute('aria-describedby');
      main.setAttribute('aria-describedby', existing ? `${existing} dg-lab-keyboard-instructions` : 'dg-lab-keyboard-instructions');
    }
  }

  function initAll() {
    document.querySelectorAll('.dg-lab').forEach(initLab);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.dg-lab').forEach((root) => {
      root.dgLabCommonCleanup?.();
      delete root.dataset.dgLabCommonInitialized;
    });
  });

  initAll();
})();
